import type { DefaultTheme } from 'styled-components';
import archiveCard from '@klv/molecules/archive-card/themes/default';
import ctaStatement from '@klv/organisms/cta-statement/themes/default';
import breadcrumbs from '@klv/molecules/breadcrumbs/themes/default';
import { bulletedList } from '@klv/organisms/bulleted-list/theme';
import bulletedLists from '@klv/organisms/bulleted-lists/themes/default';
import button from '@klv/atoms/button/themes/default';
import chart from '@klv/organisms/chart/themes/default';
import countdown from '@klv/organisms/countdown/themes/default';
import dynamicTextFormField from '@klv/molecules/dynamic-text-form-field/themes/default';
import footerCta from '@klv/organisms/footer-cta/themes/default';
import formsCta from '@klv/organisms/forms-cta/themes/default';
import gallery from '@klv/organisms/gallery/themes/default';
import galleryCard from '@klv/molecules/gallery-card/themes/default';
import greenhouseJobList from '@klv/organisms/greenhouse-job-list/themes/default';
import greenhouseJob from '@klv/molecules/greenhouse-job/themes/default';
import moreCard from '@klv/molecules/more-card/themes/default';
import notFound from '@klv/organisms/not-found/themes/default';
import pricing from '@klv/organisms/pricing/themes/default';
import relatedContentCard from '@klv/molecules/related-content-card/themes/default';
import shadows from '../shadows';
import stat from '@klv/molecules/stat/themes/default';
import stats from '@klv/organisms/stats/themes/default';
import subcategoryCard from '@klv/molecules/subcategory-card/themes/default';
import testimonialsTwoColumn from '@klv/organisms/testimonials-two-column/themes/default';
import { textTagline } from '@klv/organisms/text-tagline/themes/default';
import tileList from '@klv/organisms/tile-list/themes/default';
import topicsMenu from '@klv/organisms/topics-menu/themes/default';
import twoColumnL30R70 from '@klv/utils/two-column-l30-r70/theme/default';
import twoColumnSlider from '@klv/organisms/two-column-slider/themes/default';
import videoModalTrigger from '@klv/molecules/video-modal-trigger/themes/default';
// COMPONENT_THEME_IMPORTS
import { screenSize, breakpoints } from '@klv/shared/utils/screen-size';
import { clampSize } from '@klv/shared/utils/functions';
import { designSystemColors, otherColors } from '../colors';
import fonts from '../fonts';

import type {
  IFormText, IHeadline, ITextContent, TTextTransform,
} from '@klv/atoms/typography/variants/types';


const globalVerticalPadding = clampSize(4, 5.6);

const globalPadding = (`
  padding-top: ${globalVerticalPadding};
  padding-bottom: ${globalVerticalPadding};
`);

const transition = {
  delay: 0,
  duration: 0.125,
  easingFunction: 'ease',
  string: '',
};
transition.string = `${transition.duration}s ${transition.easingFunction} ${transition.delay}s`;

// Components

const carousel = {
  arrows: designSystemColors.charcoal,
};

const featuresList = {
  dark: {
    background: designSystemColors.charcoal,
    text: designSystemColors.white,
    borderColor: otherColors.fadedWhite,
    imageFocus: designSystemColors.poppy,
    headlineColor: designSystemColors.white,
  },
  light: {
    background: designSystemColors.lightSand,
    text: designSystemColors.charcoal,
    borderColor: otherColors.fadedCharcoal,
    imageFocus: designSystemColors.poppy,
    headlineColor: designSystemColors.charcoal,
  },
};
const footer = {
  background: designSystemColors.charcoal,
  text: designSystemColors.white,
};
const form = {
  dark: {
    label: designSystemColors.white,
    inputText: designSystemColors.white,
    error: otherColors.errorRed,
    textColor: designSystemColors.white,
    placeholderText: otherColors.placeholderText,
    fieldBackground: otherColors.inputFieldBackground,
    fieldBackgroundFocus: otherColors.inputFieldBackground,
    fieldBorder: 'rgba(255, 255, 255, 0.5)',
    fieldBoxShadow: `0 .1rem .2rem ${otherColors.inputFieldBoxShadow}`,
    checkboxDefault: designSystemColors.white,
    checkboxChecked: designSystemColors.lemon,
    checkboxHover: otherColors.checkBoxHoverBackground,
    successIcon: designSystemColors.poppy,
    beforeBorder: designSystemColors.charcoal,
  },
  light: {
    label: otherColors.inputText,
    inputText: otherColors.inputText,
    error: otherColors.errorRed,
    textColor: otherColors.inputText,
    placeholderText: otherColors.gray90,
    fieldBackground: designSystemColors.lightSand,
    fieldBackgroundFocus: designSystemColors.lightSand,
    fieldBorder: otherColors.gray70,
    fieldBoxShadow: `0 .1rem .2rem ${otherColors.fadedWhite}`,
    checkboxDefault: designSystemColors.charcoal,
    checkboxChecked: designSystemColors.charcoal,
    checkboxHover: designSystemColors.white,
    successIcon: designSystemColors.white,
    beforeBorder: designSystemColors.lightSand,
  },
};

const formTextLarge: IFormText = {
  fontFace: fonts.sohne.regular,
  fontSize: '1.6rem',
  lineHeight: 1.5,
};

const formTextRegular: IFormText = {
  fontFace: fonts.sohne.regular,
  fontSize: '1.4rem',
  lineHeight: 1.42,
};

const formTextSmall: IFormText = {
  fontFace: fonts.sohne.regular,
  fontSize: '1.2rem',
  lineHeight: 1.5,
};

const formText = {
  large: formTextLarge,
  regular: formTextRegular,
  small: formTextSmall,
};

const headlineText: IHeadline = {
  fontFace: fonts.sohne.regular,
  fontSize: clampSize(3.4, 10),
  lineHeight: 1.2,
};
const editorialHeadline: IHeadline = {
  fontFace: fonts.recklessNeue.regular,
  fontSize: clampSize(2.4, 3.2),
  lineHeight: 1.25,
};
const h1Headline: IHeadline = {
  ...headlineText,
  fontSize: clampSize(3.4, 4.8),
};
const h2Headline: IHeadline = {
  ...headlineText,
  fontSize: clampSize(2.8, 4),
};
const h3Headline: IHeadline = {
  ...headlineText,
  fontSize: clampSize(2.8, 3.2),
};
const h4Headline: IHeadline = {
  ...headlineText,
  fontSize: clampSize(2, 2.8),
};
const h5Headline: IHeadline = {
  ...headlineText,
  fontSize: clampSize(2, 2.4),
};
const h6Headline: IHeadline = {
  ...headlineText,
  fontSize: clampSize(1.6, 2),
};

const contentHeaderListView1: IHeadline = {
  fontFace: fonts.sohne.regular,
  fontSize: clampSize(2.4, 2.8),
  lineHeight: 1.2,
};

const contentHeaderListView2: IHeadline = {
  fontFace: fonts.sohne.regular,
  fontSize: clampSize(2.0, 2.4),
  lineHeight: 1.2,
};

const global = {
  dark: {
    backgroundColor: designSystemColors.charcoal,
    text: designSystemColors.white,
  },
  light: {
    backgroundColor: designSystemColors.lightSand,
    text: designSystemColors.charcoal,
  },
};

const headline = {
  articleLeadIn: {
    fontFace: fonts.recklessNeue.regular,
    fontSize: clampSize(4, 5.7),
    lineHeight: '7rem',
  },
  chartHeadline: {
    fontFace: fonts.sohneMono.regular,
    fontSize: '1.8rem',
    lineHeight: 2.4,
  },
  contentHeaderListView1,
  contentHeaderListView2,
  display1: headlineText,
  display2: {
    ...headlineText,
    fontSize: clampSize(3.4, 8.2),
    lineHeight: 1.09,
  },
  display3: {
    ...headlineText,
    fontSize: clampSize(3.4, 5.8),
  },
  editorial: editorialHeadline,
  h1: h1Headline,
  h1Semibold: {
    ...h1Headline,
    fontFace: fonts.sohne.semibold,
  },
  h2: h2Headline,
  h2Semibold: {
    ...h2Headline,
    fontFace: fonts.sohne.semibold,
  },
  h3: {
    ...h3Headline,
  },
  h3Semibold: {
    ...h3Headline,
    fontFace: fonts.sohne.semibold,
  },
  h4: h4Headline,
  h4Semibold: {
    ...h4Headline,
    fontFace: fonts.sohne.semibold,
  },
  h5: h5Headline,
  h5Semibold: {
    ...h5Headline,
    fontFace: fonts.sohne.semibold,
  },
  h6: h6Headline,
  h6Semibold: {
    ...h6Headline,
    fontFace: fonts.sohne.semibold,
  },
  quoteLarge: {
    ...editorialHeadline,
    fontSize: clampSize(3.4, 4.8),
  },
  quoteSmall: editorialHeadline,
  quoteSmallWhite: {
    ...editorialHeadline,
    color: designSystemColors.white,
  },
  quoteLargeWhite: {
    ...editorialHeadline,
    fontSize: clampSize(3.4, 4.8),
    color: designSystemColors.white,
  },
};

const iframe = {
  dark: {
    backgroundColor: designSystemColors.charcoal,
  },
  light: {
    backgroundColor: designSystemColors.lightSand,
  },
};
const logos = {
  charcoal: {
    backgroundColor: designSystemColors.charcoal,
    button: 'light',
    subhead: designSystemColors.white,
    headline: designSystemColors.white,
  },
  lightSand: {
    backgroundColor: designSystemColors.lightSand,
    button: 'dark',
    subhead: designSystemColors.charcoal,
    headline: designSystemColors.charcoal,
  },
  dark: {
    backgroundColor: designSystemColors.charcoal,
    button: 'light',
    subhead: designSystemColors.white,
    headline: designSystemColors.white,
  },
  light: {
    backgroundColor: designSystemColors.lightSand,
    button: 'dark',
    subhead: designSystemColors.charcoal,
    headline: designSystemColors.charcoal,
  },
};
const outline = {
  colors: {
    dark: {
      color: designSystemColors.charcoal,
    },
    light: {
      color: designSystemColors.white,
    },
  },
  global: {
    offset: '2px',
    size: '1px',
    style: 'dashed',
  },
};
const tagline = {
  desktopFontSize: '1.6rem',
  desktopLineHeight: '2.4rem',
  fontSize: '1.4rem',
  lineHeight: '2.2rem',
};
const textContentFont: ITextContent = {
  desktopFontSize: '1.8rem',
  fontFace: fonts.sohne.regular,
  fontSize: '1.6rem',
  lineHeight: 1.5,
};
const textContent = {
  one: textContentFont,
  oneSemibold: {
    ...textContentFont,
    fontFace: fonts.sohne.semibold,
  },
  two: {
    ...textContentFont,
    desktopFontSize: '1.6rem',
  },
  twoSemibold: {
    ...textContentFont,
    desktopFontSize: '1.6rem',
    fontFace: fonts.sohne.semibold,
  },
  three: {
    ...textContentFont,
    desktopFontSize: '1.4rem',
    fontSize: '1.4rem',
  },
  threeSemibold: {
    ...textContentFont,
    desktopFontSize: '1.4rem',
    fontFace: fonts.sohne.semibold,
    fontSize: '1.4rem',
  },
};
const relatedContent = {
  dark: {
    background: designSystemColors.charcoal,
    text: designSystemColors.white,
  },
  light: {
    background: designSystemColors.lightSand,
    text: designSystemColors.charcoal,
  },
};
const twoColumn = {
  light: {
    background: designSystemColors.lightSand,
    eyebrow: designSystemColors.charcoal,
    text: designSystemColors.charcoal,
  },
  dark: {
    background: designSystemColors.charcoal,
    eyebrow: designSystemColors.lemon,
    text: designSystemColors.white,
  },
  lightSand: {
    background: designSystemColors.lightSand,
    eyebrow: designSystemColors.charcoal,
    text: designSystemColors.charcoal,
  },
  charcoal: {
    background: designSystemColors.charcoal,
    eyebrow: designSystemColors.lemon,
    text: designSystemColors.white,
  },
};

const typography = {
  colors: {
    dark: {
      bullets: designSystemColors.poppy,
      text: designSystemColors.charcoal,
    },
    light: {
      bullets: designSystemColors.lemon,
      text: designSystemColors.white,
    },
  },
  variants: {
    articleLeadIn: headline.articleLeadIn,
    chartHeadline: headline.chartHeadline,
    contentHeaderListView1: headline.contentHeaderListView1,
    contentHeaderListView2: headline.contentHeaderListView2,
    display1: headline.display1,
    display2: headline.display2,
    display3: headline.display3,
    disclaimer: {
      fontFace: fonts.sohne.regular,
      fontSize: '1.2rem',
      lineHeight: 1.67,
    },
    editorial: editorialHeadline,
    eyebrow: {
      fontFace: fonts.sohneMono.regular,
      fontSize: '1.2rem',
      letterSpacing: '0.2rem',
      textTransform: 'uppercase' as TTextTransform,
      lineHeight: '1.5',
    },
    h1: headline.h1,
    h1Semibold: headline.h1Semibold,
    h2: headline.h2,
    h2Semibold: headline.h2Semibold,
    h3: headline.h3,
    h3Semibold: headline.h3Semibold,
    h4: headline.h4,
    h4Semibold: headline.h4Semibold,
    h5: headline.h5,
    h5Semibold: headline.h5Semibold,
    h6: h6Headline,
    h6Semibold: headline.h6Semibold,
    formLarge: formText.large,
    formRegular: formText.regular,
    formSmall: formText.small,
    navigation: {
      desktopFontSize: '2.4rem',
      fontFace: fonts.sohneMono.regular,
      fontSize: '1.6rem',
      letterSpacing: '0.2rem',
      lineHeight: '2rem',
    },
    paragraph1: textContent.one,
    paragraph1Semibold: textContent.oneSemibold,
    paragraph2: textContent.two,
    paragraph2Semibold: textContent.twoSemibold,
    paragraph3: textContent.three,
    paragraph3Semibold: textContent.threeSemibold,
    quoteLarge: headline.quoteLarge,
    quoteSmall: headline.quoteSmall,
    tagline: {
      ...tagline,
      fontFace: fonts.sohne.regular,
    },
    taglineSemibold: {
      ...tagline,
      fontFace: fonts.sohne.semibold,
    },
    testimonialsQuote: {
      fontFace: fonts.recklessNeue.regular,
      fontSize: clampSize(4, 4.8),
      lineHeight: 1.42,
    },
  },
};

const socialProof = {
  mainBackground: {
    light: designSystemColors.lightSand,
    dark: designSystemColors.charcoal,
  },
  copyContentBackground: {
    poppy: designSystemColors.poppy,
    lavender: designSystemColors.lavender,
    sage: designSystemColors.sage,
    violet: designSystemColors.violet,
    eggplant: designSystemColors.eggplant,
  },
};
const instantSearch = {
  ...typography.variants.paragraph2,
  text: designSystemColors.charcoal,
  background: designSystemColors.white,
  hover: {
    background: designSystemColors.sandTint,
  },
};

// Themes
const theme: DefaultTheme = {
  name: 'Default',
  fonts,
  globalPadding,
  globalVerticalPadding,
  screenSize,
  transition,
  colors: {
    ...designSystemColors,
    ...otherColors,
  },
  archiveCard,
  ctaStatement,
  breadcrumbs,
  breakpoints: [...breakpoints],
  bulletedList,
  bulletedLists,
  button,
  carousel,
  chart,
  countdown,
  dynamicTextFormField,
  featuresList,
  footer,
  footerCta,
  form,
  formsCta,
  formText,
  gallery,
  galleryCard,
  greenhouseJobList,
  greenhouseJob,
  global,
  headline,
  iframe,
  instantSearch,
  logos,
  moreCard,
  notFound,
  outline,
  pricing,
  relatedContent,
  relatedContentCard,
  shadows,
  stat,
  stats,
  subcategoryCard,
  testimonialsTwoColumn,
  textContent,
  textTagline,
  tileList,
  topicsMenu,
  twoColumn,
  twoColumnL30R70,
  twoColumnSlider,
  typography,
  videoModalTrigger,
  socialProof,
  // COMPONENT_THEME_ENTRIES
};

export {
  fonts,
  screenSize,
};

export default theme;
